// src/pages/components/ProjectCalculationsCard.js
import { useEffect, useMemo, useState } from 'react';
import { Paper, Stack, Typography, Button, Box, Divider, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getToolTypeLabel } from '../toolLabels';
import AttachCalculationsDialog from './AttachCalculationsDialog';

const ProjectDetailsCalculationsCard = ({ projectId }) => {
  const navigate = useNavigate();
  const TOOL_ROUTES = {
    ClayEffectiveFrictionAngle: '/calculations/clayEffectiveFrictionAngle',
    Consolidation: '/calculations/consolidation',
    CvLLUsNavy: '/calculations/cvLLUsNavy',
    Hansen: '/calculations/hansen',
    LayeredSoil: '/calculations/layered-soil',
    Liquefaction: '/calculations/liquefaction-tbdy',
    MohrCircle: '/calculations/mohr-circle',
    Meyerhof: '/calculations/meyerhof',
    Pilerockbc: '/calculations/pile-rock-bc',
    Pilerocksettle: '/calculations/pile-rock-settle-aashto2002',
    Priebe: '/calculations/priebe',
    Sptcorrection: '/calculations/spt-corr',
    Stroud: '/calculations/stroud',
    StressIncrease: '/calculations/stressIncrease',
    SlopeStability: '/calculations/slope-stability',
    Terzaghi: '/calculations/terzaghi',
    UscsClassifier: '/calculations/uscsClassifier',
    Vs30SiteClassEc8: '/calculations/vs30SiteClassEc8',
    WeightVolumeRelationships: '/calculations/weightVolumeRelations',
  };

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // CalcIndex rows
  const [error, setError] = useState('');

  // Row menu state (must track which row)
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [attachOpen, setAttachOpen] = useState(false);

  const fetchProjectCalcs = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`/api/v1/project/${projectId}/calculations`);
      const data = res?.data?.data?.data || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load project calculations.';
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectCalcs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Turn CalcIndex items into table rows
  const rows = useMemo(() => {
    // items look like: { _id, toolKey: "Terzaghi", calcId, project, updatedAt, ... }
    return items.map((c, i) => ({
      id: c._id || i,
      toolKey: c.toolKey,
      calcId: c.calcId,
      name: c.calcName,
      type: getToolTypeLabel(c.toolKey),
    }));
  }, [items]);

  const handleOpenMenu = (e, row) => {
    setAnchorEl(e.currentTarget);
    setActiveRow(row);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveRow(null);
  };

  const handleView = () => {
    if (!activeRow) return;
    const route = TOOL_ROUTES[activeRow.toolKey];
    if (!route) {
      handleCloseMenu();
      return;
    }

    navigate(`${route}?saved=${activeRow.calcId}`);

    handleCloseMenu();
  };

  const handleDetach = async () => {
    if (!activeRow) return;

    try {
      await axios.post(`/api/v1/project/${projectId}/detach-calc`, {
        toolKey: activeRow.toolKey,
        calcId: activeRow.calcId,
      });
      handleCloseMenu();
      fetchProjectCalcs();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to detach calculation.';
      setError(msg);
      handleCloseMenu();
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'relative', mb: 5 }}>
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{
          position: 'absolute',
          top: -8,
          left: 12,
          backgroundColor: 'background.paper',
          px: 0.5,
          fontWeight: 700,
        }}
      >
        Calculations
      </Typography>

      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 1fr 120px',
          gap: 1,
          px: 1,
          pb: 0.5,
          fontSize: 14,
          color: 'text.secondary',
        }}
      >
        <Box>#</Box>
        <Box>Calculation Name</Box>
        <Box>Calculation Type</Box>
        <Box>Actions</Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Rows */}
      <Stack spacing={0.75}>
        {!loading && rows.length === 0 ? (
          <Box sx={{ px: 1, py: 1 }} color="text.secondary">
            No calculations attached yet.
          </Box>
        ) : (
          rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1fr 120px',
                gap: 1,
                px: 1,
                alignItems: 'center',
                fontSize: 14,
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Box color="text.secondary">{index + 1}</Box>

              <Box
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={row.name}
              >
                {row.name}
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                }}
                title={row.type}
              >
                {row.type}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)} disabled={loading}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Stack>

      {/* Single menu, driven by activeRow */}
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
        <MenuItem onClick={handleView} disabled={!activeRow || !TOOL_ROUTES[activeRow.toolKey]}>
          View
        </MenuItem>
        <MenuItem onClick={handleDetach}>Detach</MenuItem>
      </Menu>

      {/* Attach button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button variant="outlined" size="small" onClick={() => setAttachOpen(true)}>
          Attach calculations
        </Button>
      </Box>

      <AttachCalculationsDialog
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        projectId={projectId}
        onApplied={fetchProjectCalcs}
      />

      {!!error && (
        <Box sx={{ mt: 1 }} color="error.main">
          {error}
        </Box>
      )}
    </Paper>
  );
};

export default ProjectDetailsCalculationsCard;
